import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { getHelperProfile, type HelperProfileBundle, type HelperReview } from "@/lib/helpers.functions";
import { InstantBookPanel } from "@/components/site/InstantBookPanel";
import { FavoriteButton } from "@/components/site/FavoriteButton";
import { TrustScore, TrustScoreBreakdown, computeTrustBreakdown } from "@/components/site/TrustScore";
import { TrustBadge } from "@/components/brand/TrustBadge";
import {
  Star, BadgeCheck, ShieldCheck, Clock, MapPin, Sparkles, MessageCircle,
  Zap, CheckCircle2, Award, Calendar, Users, ThumbsUp, ChevronDown, AlertCircle, Loader2,
} from "lucide-react";

export const Route = createFileRoute("/helpers/$helperId")({
  loader: async ({ params }) => {
    const helper = await getHelperProfile({ data: { helperId: params.helperId } });
    if (!helper) throw notFound();
    return { helper };
  },
  head: ({ loaderData }) => {
    const h = loaderData?.helper;
    if (!h) return { meta: [{ title: "Helper — Peopels" }] };
    const title = `${h.display_name}${h.headline ? ` — ${h.headline}` : ""} · Peopels`;
    const ratingFragment = h.rating_count > 0 ? `${h.avg_rating.toFixed(2)}★ from ${h.rating_count} review${h.rating_count === 1 ? "" : "s"} · ` : "";
    const jobsFragment = h.jobs_completed > 0 ? `${h.jobs_completed} job${h.jobs_completed === 1 ? "" : "s"} completed${h.city ? ` in ${h.city}` : ""}. ` : (h.city ? `Based in ${h.city}. ` : "");
    const desc = `${h.display_name} · ${ratingFragment}${jobsFragment}Book a vetted Helper on Peopels.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        ...(h.avatar_url ? [{ property: "og:image", content: h.avatar_url }] : []),
      ],
    };
  },
  errorComponent: ({ reset }) => (
    <ErrorState
      title="We couldn't load this profile"
      body="Try again in a moment — if it keeps happening, the Helper may have paused their account."
      onRetry={reset}
    />
  ),
  notFoundComponent: () => (
    <ErrorState
      title="Helper not found"
      body="This Helper isn't available right now. Browse other vetted Helpers near you."
    />
  ),
  component: HelperProfilePage,
});

function HelperProfilePage() {
  const { helper } = Route.useLoaderData();
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const [reviewSort, setReviewSort] = useState<"helpful" | "recent">("helpful");

  // Funnel: profile view
  useEffect(() => {
    void import("@/lib/funnel-track").then(({ track }) =>
      track("helper_profile_view", {
        helper_id: helper.id,
        rating: helper.avg_rating,
        reviews: helper.rating_count,
        jobs_completed: helper.jobs_completed,
      }),
    );
  }, [helper.id, helper.avg_rating, helper.rating_count, helper.jobs_completed]);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-6xl flex-1 px-4 pb-32 pt-6 sm:px-6 sm:pb-12 sm:pt-10 lg:px-8">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <ProfileHero helper={helper} />

        {/* ── Body grid ────────────────────────────────────────────────── */}
        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
          <div className="min-w-0 space-y-8">
            <TrustStrip helper={helper} />
            {(helper.about || helper.bio) && (
              <Section title="About">
                <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/90">
                  {helper.about || helper.bio}
                </p>
              </Section>
            )}
            {helper.skills.length > 0 && (
              <Section title="Services">
                <ul className="flex flex-wrap gap-2">
                  {helper.skills.map((s: { id: string; name: string; icon: string | null }) => (
                    <li key={s.id} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm">
                      {s.icon && <span aria-hidden>{s.icon}</span>}
                      {s.name}
                    </li>
                  ))}
                </ul>
              </Section>
            )}
            {helper.badges.length > 0 && (
              <Section title="Recognition">
                <ul className="flex flex-wrap gap-2">
                  {helper.badges.map((b: { id: string; name: string }) => (
                    <li key={b.id} className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-300">
                      <Award className="h-3.5 w-3.5" /> {b.name}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <ReviewsBlock
              helper={helper}
              expanded={reviewsExpanded}
              onToggle={() => setReviewsExpanded((e) => !e)}
              sort={reviewSort}
              onSortChange={setReviewSort}
            />
          </div>

          {/* Sticky booking card (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-4">
              <BookingCard helper={helper} />
              {helper.instant_book_enabled && helper.available && (
                <InstantBookPanel helperId={helper.id} />
              )}
            </div>
          </aside>
        </div>

        {/* Instant book on mobile (under main content) */}
        {helper.instant_book_enabled && helper.available && (
          <div className="mt-8 lg:hidden">
            <InstantBookPanel helperId={helper.id} />
          </div>
        )}
      </main>

      {/* Sticky mobile booking bar */}
      <MobileBookingBar helper={helper} />

      <Footer />
    </div>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────
function ProfileHero({ helper }: { helper: HelperProfileBundle }) {
  const initials = helper.display_name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <section className="overflow-hidden rounded-3xl border border-border bg-card shadow-card">
      <div className="h-20 bg-gradient-to-br from-primary/15 via-accent/30 to-background sm:h-28" aria-hidden />
      <div className="-mt-12 px-4 pb-5 pt-0 sm:-mt-14 sm:px-7 sm:pb-7">
        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-end gap-4 sm:flex sm:items-end sm:gap-5">
          <div className="relative">
            {helper.avatar_url ? (
              <img
                src={helper.avatar_url}
                alt={`Profile photo of ${helper.display_name}`}
                width={120}
                height={120}
                className="h-24 w-24 shrink-0 rounded-full object-cover ring-4 ring-card sm:h-28 sm:w-28"
              />
            ) : (
              <span className="grid h-24 w-24 shrink-0 place-items-center rounded-full bg-accent text-2xl font-bold text-primary ring-4 ring-card sm:h-28 sm:w-28">
                {initials}
              </span>
            )}
            {helper.id_verified && (
              <span
                className="absolute -bottom-1 -right-1 grid h-7 w-7 place-items-center rounded-full bg-emerald-500 text-white ring-2 ring-card"
                aria-label="Identity verified"
              >
                <BadgeCheck className="h-4 w-4" />
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1 pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate font-display text-2xl font-bold sm:text-3xl">{helper.display_name}</h1>
              {helper.featured && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                  <Sparkles className="h-3 w-3" /> Featured
                </span>
              )}
              {helper.available ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Available now
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  Currently busy
                </span>
              )}
            </div>
            {helper.headline && <p className="mt-1 text-sm text-muted-foreground">{helper.headline}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {helper.rating_count > 0 ? (
                <span className="inline-flex items-center gap-1 text-amber-600">
                  <Star className="h-4 w-4 fill-current" />
                  <strong className="text-foreground">{helper.avg_rating.toFixed(2)}</strong>
                  <span className="text-muted-foreground">({helper.rating_count})</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Star className="h-4 w-4 opacity-40" /> No reviews yet
                </span>
              )}
              <TrustScore score={helper.trust_score} size="sm" />

              {helper.city && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" /> {helper.city}
                </span>
              )}
              {helper.years_experience != null && helper.years_experience > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" /> {helper.years_experience}+ yrs experience
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {helper.id_verified && helper.background_check_passed && <TrustBadge variant="trusted-professional" />}
              {helper.id_verified && !helper.background_check_passed && <TrustBadge variant="id-verified" />}
              {helper.background_check_passed && <TrustBadge variant="verified-professional" />}
              {helper.avg_rating >= 4.8 && helper.jobs_completed >= 20 && <TrustBadge variant="top-rated" />}
            </div>
          </div>
          <div className="hidden shrink-0 pt-3 sm:block">
            <FavoriteButton helperId={helper.id} helperName={helper.display_name} variant="pill" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Trust strip (key stats) ────────────────────────────────────────────
function TrustStrip({ helper }: { helper: HelperProfileBundle }) {
  const response = helper.response_time_minutes
    ? helper.response_time_minutes < 60
      ? `${helper.response_time_minutes} min`
      : `${Math.round(helper.response_time_minutes / 60)} hr`
    : "—";
  const completion = helper.completion_rate != null ? `${Math.round(Number(helper.completion_rate) * (helper.completion_rate <= 1 ? 100 : 1))}%` : "—";
  const repeat = helper.repeat_booking_rate != null ? `${Math.round(Number(helper.repeat_booking_rate) * (helper.repeat_booking_rate <= 1 ? 100 : 1))}%` : null;
  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-card sm:p-5">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat icon={<CheckCircle2 className="h-4 w-4" />} label="Jobs completed" value={String(helper.jobs_completed)} />
        <Stat icon={<Clock className="h-4 w-4" />} label="Avg. response" value={response} />
        <Stat icon={<ThumbsUp className="h-4 w-4" />} label="Completion rate" value={completion} />
        <Stat
          icon={<Users className="h-4 w-4" />}
          label={repeat ? "Repeat customers" : "Rating"}
          value={repeat ?? (helper.rating_count > 0 ? `${helper.avg_rating.toFixed(1)} ★` : "—")}
        />
      </div>
      <ul className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4 text-xs">
        {helper.id_verified && <TrustChip icon={<BadgeCheck className="h-3.5 w-3.5" />} label="ID verified" />}
        {helper.background_check_passed && <TrustChip icon={<ShieldCheck className="h-3.5 w-3.5" />} label="DBS background check" />}
        
        <TrustChip icon={<ShieldCheck className="h-3.5 w-3.5" />} label="Escrow protected payments" />
        {helper.instant_book_enabled && <TrustChip icon={<Zap className="h-3.5 w-3.5" />} label="Instant book" />}
      </ul>
      <div className="mt-4 border-t border-border pt-4">
        <TrustScoreBreakdown
          {...computeTrustBreakdown({
            id_verified: helper.id_verified,
            background_check_passed: helper.background_check_passed,
            kyc_reviewed_at: null,
            jobs_completed: helper.jobs_completed,
            avg_rating: helper.avg_rating,
            rating_count: helper.rating_count,
            response_time_minutes: helper.response_time_minutes,
            completion_rate: helper.completion_rate,
            repeat_booking_rate: helper.repeat_booking_rate,
          })}
          total={helper.trust_score}
        />
      </div>

    </section>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div>
      <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
        <span className="text-primary">{icon}</span> {label}
      </p>
      <p className="mt-1 font-display text-xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function TrustChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <li className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 font-medium text-emerald-700 dark:text-emerald-300">
      {icon} {label}
    </li>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5 shadow-card sm:p-6">
      <h2 className="mb-3 font-display text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

// ─── Reviews block ─────────────────────────────────────────────────────
function ReviewsBlock({
  helper, expanded, onToggle, sort, onSortChange,
}: {
  helper: HelperProfileBundle;
  expanded: boolean;
  onToggle: () => void;
  sort: "helpful" | "recent";
  onSortChange: (s: "helpful" | "recent") => void;
}) {
  const totalRated = Object.values(helper.rating_breakdown).reduce((a, b) => a + b, 0);
  let reviews: HelperReview[] = [...helper.reviews];
  if (sort === "helpful") {
    // "Most helpful" approx: 5-star with longest comment first, then by rating, then by recency
    reviews.sort((a, b) => {
      const ascore = (a.rating >= 5 ? 100 : 0) + Math.min((a.comment?.length ?? 0) / 20, 50);
      const bscore = (b.rating >= 5 ? 100 : 0) + Math.min((b.comment?.length ?? 0) / 20, 50);
      if (bscore !== ascore) return bscore - ascore;
      return +new Date(b.created_at) - +new Date(a.created_at);
    });
  } else {
    reviews.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
  }
  const visible = expanded ? reviews : reviews.slice(0, 4);

  return (
    <section className="rounded-2xl border border-border bg-card p-5 shadow-card sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Reviews</h2>
          {helper.rating_count > 0 ? (
            <p className="mt-0.5 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1 text-amber-600">
                <Star className="h-4 w-4 fill-current" />
                <strong className="text-foreground">{helper.avg_rating.toFixed(2)}</strong>
              </span>{" "}
              from <strong className="text-foreground">{helper.rating_count}</strong> verified booking{helper.rating_count === 1 ? "" : "s"}
            </p>
          ) : (
            <p className="mt-0.5 text-sm text-muted-foreground">No reviews yet — this Helper hasn't been reviewed.</p>
          )}
        </div>
        <div className="inline-flex rounded-lg border border-border bg-muted p-0.5 text-xs">
          {(["helpful", "recent"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onSortChange(s)}
              className={`rounded-md px-2.5 py-1 font-medium transition ${sort === s ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
            >
              {s === "helpful" ? "Most helpful" : "Most recent"}
            </button>
          ))}
        </div>
      </div>

      {/* Rating breakdown bars */}
      {totalRated > 0 && (
        <ul className="mt-4 space-y-1.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = helper.rating_breakdown[star as 1 | 2 | 3 | 4 | 5];
            const pct = totalRated ? (count / totalRated) * 100 : 0;
            return (
              <li key={star} className="grid grid-cols-[28px_minmax(0,1fr)_36px] items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-0.5 font-medium text-foreground">{star}<Star className="h-3 w-3 fill-current text-amber-500" /></span>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-right text-muted-foreground tabular-nums">{count}</span>
              </li>
            );
          })}
        </ul>
      )}

      {reviews.length === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No reviews yet — be the first to book and review {helper.display_name}.
        </div>
      ) : (
        <>
          <ul className="mt-6 divide-y divide-border">
            {visible.map((r) => (
              <ReviewItem key={r.id} review={r} />
            ))}
          </ul>
          {reviews.length > 4 && (
            <button
              type="button"
              onClick={onToggle}
              className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              {expanded ? "Show fewer reviews" : `Show all ${reviews.length} reviews`}
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          )}
        </>
      )}
    </section>
  );
}

function ReviewItem({ review }: { review: HelperReview }) {
  const initials = (review.reviewer_name ?? "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        {review.reviewer_avatar ? (
          <img src={review.reviewer_avatar} alt="" loading="lazy" className="h-9 w-9 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-xs font-semibold text-primary">{initials}</span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <p className="text-sm font-semibold text-foreground">{review.reviewer_name ?? "Verified customer"}</p>
            <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-2.5 w-2.5" /> Verified booking
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-0.5 text-amber-500">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`h-3 w-3 ${i < review.rating ? "fill-current" : "opacity-25"}`} />
              ))}
            </span>
            <span>·</span>
            <time dateTime={review.created_at}>{new Date(review.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}</time>
            {review.category_name && <><span>·</span><span>{review.category_name}</span></>}
          </div>
          {review.comment && <p className="mt-2 text-sm text-foreground/90">{review.comment}</p>}
        </div>
      </div>
    </li>
  );
}

// ─── Booking card / mobile bar ──────────────────────────────────────────
function BookingCard({ helper }: { helper: HelperProfileBundle }) {
  const fromRate = helper.hourly_rate ? `£${helper.hourly_rate.toFixed(0)}` : "Quote";
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-baseline justify-between">
        <p>
          <span className="font-display text-3xl font-bold">{fromRate}</span>
          {helper.hourly_rate && <span className="ml-1 text-sm text-muted-foreground">/ hour</span>}
        </p>
        {helper.rating_count > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
            <Star className="h-3.5 w-3.5 fill-current" /> {helper.avg_rating.toFixed(2)} ({helper.rating_count})
          </span>
        ) : (
          <span className="text-xs font-medium text-muted-foreground">New Helper</span>
        )}
      </div>
      <Button asChild size="lg" className="mt-4 w-full">
        <Link
          to="/post-job"
          search={{ helper: helper.id } as never}
          onClick={() => {
            void import("@/lib/funnel-track").then(({ track }) =>
              track("helper_book_click", { helper_id: helper.id, location: "sidebar" }),
            );
          }}
        >
          {helper.instant_book_enabled ? <><Zap className="mr-1.5 h-4 w-4" /> Book instantly</> : "Request a quote"}
        </Link>
      </Button>
      <Button asChild variant="outline" size="lg" className="mt-2 w-full">
        <Link
          to="/post-job"
          search={{ helper: helper.id, message: 1 } as never}
          onClick={() => {
            void import("@/lib/funnel-track").then(({ track }) =>
              track("helper_message_click", { helper_id: helper.id }),
            );
          }}
        >
          <MessageCircle className="mr-1.5 h-4 w-4" /> Message {helper.display_name.split(" ")[0]}
        </Link>
      </Button>
      <ul className="mt-4 space-y-2 text-xs text-muted-foreground">
        <li className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Payment held in escrow until job done</li>
        <li className="flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5 text-primary" /> ID & DBS verified Helper</li>
        <li className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-primary" /> Free cancellation up to 12h before</li>
      </ul>
      <p className="mt-3 text-center text-[11px] text-muted-foreground">Your card is securely authorized at booking. Funds are only released once you confirm the job is done.</p>
    </div>
  );
}

function MobileBookingBar({ helper }: { helper: HelperProfileBundle }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-4 py-3 backdrop-blur lg:hidden">
      <div className="mx-auto flex max-w-2xl items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            {helper.hourly_rate ? `From £${helper.hourly_rate.toFixed(0)}/hr` : "Quote in minutes"}
          </p>
          <p className="truncate text-[11px] text-muted-foreground">
            {helper.rating_count > 0 ? (
              <>
                <Star className="mr-0.5 inline h-3 w-3 fill-current text-amber-500" />
                {helper.avg_rating.toFixed(2)} · {helper.jobs_completed} jobs · Escrow protected
              </>
            ) : (
              <>{helper.jobs_completed > 0 ? `${helper.jobs_completed} jobs · ` : ""}Escrow protected</>
            )}
          </p>
        </div>
        <Button asChild size="lg" className="shrink-0">
          <Link
            to="/post-job"
            search={{ helper: helper.id } as never}
            onClick={() => {
              void import("@/lib/funnel-track").then(({ track }) =>
                track("helper_book_click", { helper_id: helper.id, location: "mobile_bar" }),
              );
            }}
          >
            {helper.instant_book_enabled ? "Book now" : "Get quote"}
          </Link>
        </Button>
      </div>
    </div>
  );
}

// ─── Fallback states ───────────────────────────────────────────────────
function ErrorState({ title, body, onRetry }: { title: string; body: string; onRetry?: () => void }) {
  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main className="mx-auto w-full max-w-xl flex-1 px-4 py-20 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 font-display text-2xl font-bold">{title}</h1>
        <p className="mt-2 text-muted-foreground">{body}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {onRetry && (
            <Button onClick={onRetry}>
              <Loader2 className="mr-1 h-4 w-4" /> Try again
            </Button>
          )}
          <Button asChild variant="outline">
            <Link to="/browse">Browse Helpers</Link>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
